import * as R from "@totalsoft/rules-algebra";
import * as V from "@totalsoft/pure-validations";
import { sumBy } from "lodash";
import { isPositive } from "utils/simple-validations/validations";

export const getQuoteRules = () => {
  return R.fromModel((quote) =>
    R.shape({
      quoteAssets: R.items(
        R.shape({
          assetNetPrice:
            R.computed((asset) => {
              if (!quote?.exchangeRate) return 0;
              const value = (asset.equivalentAssetNetPrice || 0) * (quote?.exchangeRate || 0);
              return parseFloat(value.toFixed(2));
            }) |> R.when(R.propertiesChanged((asset) => [asset.equivalentAssetNetPrice])),
          equivalentAssetNetPrice:
            R.computed((asset) => {
              if (!quote?.exchangeRate) return 0;
              const value = (asset.assetNetPrice || 0) / quote.exchangeRate;
              return parseFloat(value.toFixed(2));
            }) |> R.when(R.propertiesChanged((asset) => [asset.assetNetPrice])),
          assetNetPriceWithVAT:
            R.computed((asset) => {
              const value = (asset.equivalentAssetNetPriceWithVAT || 0) * (quote?.exchangeRate || 0);
              return parseFloat(value.toFixed(2));
            }) |> R.when(R.propertiesChanged((asset) => [asset.equivalentAssetNetPriceWithVAT])),
          equivalentAssetNetPriceWithVAT:
            R.computed((asset) => {
              if (!quote?.exchangeRate) return 0;
              const value = (asset.assetNetPriceWithVAT || 0) / quote?.exchangeRate;
              return parseFloat(value.toFixed(2));
            }) |> R.when(R.propertiesChanged((asset) => [asset.assetNetPriceWithVAT])),
          additionalCosts:
            R.computed((asset) => {
              if (asset.assetNetPrice <= 0 || !quote?.exchangeRate) return 0;
              const value = (asset.equivalentAdditionalCosts || 0) * (quote?.exchangeRate || 0);
              return parseFloat(value.toFixed(2));
            }) |> R.when(R.propertiesChanged((asset) => [asset.equivalentAdditionalCosts, asset.assetNetPrice])),
          equivalentAdditionalCosts:
            R.computed((asset) => {
              if (asset.equivalentAssetNetPrice <= 0 || !quote?.exchangeRate) return 0;
              const value = (asset.additionalCosts || 0) / quote?.exchangeRate;
              return parseFloat(value.toFixed(2));
            }) |> R.when(R.propertiesChanged((asset) => [asset.additionalCosts, asset.equivalentAssetNetPrice])),
          additionalCostsWithVAT:
            R.computed((asset) => {
              if (asset.assetNetPriceWithVAT || !quote?.exchangeRate) return 0;
              const value = (asset.equivalentAdditionalCostsWithVAT || 0) * quote?.exchangeRate;
              return parseFloat(value.toFixed(2));
            })
            |> R.when(
              R.propertiesChanged((asset) => [asset.equivalentAdditionalCostsWithVAT, asset.assetNetPriceWithVAT])
            ),
          equivalentAdditionalCostsWithVAT:
            R.computed((asset) => {
              if (asset.equivalentAssetNetPriceWithVAT <= 0 || !quote?.exchangeRate) return 0;
              const value = (asset.additionalCostsWithVAT || 0) / quote?.exchangeRate;
              return parseFloat(value.toFixed(2));
            })
            |> R.when(
              R.propertiesChanged((asset) => [asset.additionalCostsWithVAT, asset.equivalentAssetNetPriceWithVAT])
            ),
        })
      ),
      totalAssetNetPrice:
        R.computed((quote) => {
          const value = sumBy(quote.quoteAssets, (quoteAsset) => quoteAsset?.assetNetPrice * quoteAsset?.qtty || 0);
          return value;
        }) |> R.when(R.propertiesChanged((quote) => [quote.quoteAssets])),
      totalAdditionalCosts:
        R.computed((quote) => {
          const value = sumBy(quote.quoteAssets, (quoteAsset) => quoteAsset?.additionalCosts * quoteAsset?.qtty || 0);
          return value;
        }) |> R.when(R.propertiesChanged((quote) => [quote.quoteAssets])),
      totalAmount:
        R.computed((quote) => {
          const value = quote.totalAssetNetPrice + quote.totalAdditionalCosts;
          return value;
        }) |> R.when(R.propertiesChanged((quote) => [quote.quoteAssets])),
      totalAssetNetPriceWithVAT:
        R.computed((quote) => {
          if (!quote?.showAmountsWithVAT) return 0;
          const value = sumBy(
            quote.quoteAssets,
            (quoteAsset) => quoteAsset?.assetNetPriceWithVAT * quoteAsset?.qtty || 0
          );
          return value;
        }) |> R.when(R.propertiesChanged((quote) => [quote.quoteAssets, quote.showAmountsWithVAT])),
      totalAdditionalCostsWithVAT:
        R.computed((quote) => {
          if (!quote?.showAmountsWithVAT) return 0;
          const value = sumBy(
            quote.quoteAssets,
            (quoteAsset) => quoteAsset?.additionalCostsWithVAT * quoteAsset?.qtty || 0
          );
          return value;
        }) |> R.when(R.propertiesChanged((quote) => [quote.quoteAssets, quote.showAmountsWithVAT])),
      totalAmountWithVAT:
        R.computed((quote) => {
          if (!quote?.showAmountsWithVAT) return 0;
          const value = quote.totalAssetNetPriceWithVAT + quote.totalAdditionalCostsWithVAT;
          return value;
        }) |> R.when(R.propertiesChanged((quote) => [quote.quoteAssets, quote.showAmountsWithVAT])),
    })
  );
};

export const getQuoteValidator = (showValuesInEuro) =>
  V.fromModel((model) => {
    return V.shape({
      estimationName: V.required |> V.when(model.isEstimation),
      quoteAssets: V.items(
        V.fromModel(() =>
          V.shape({
            qtty: isPositive(),
            assetNetPrice: isPositive() |> V.when(!model.showAmountsWithVAT),
            equivalentAssetNetPrice: isPositive() |> V.when(showValuesInEuro && !model.showAmountsWithVAT),
            assetNetPriceWithVAT: isPositive() |> V.when(model.showAmountsWithVAT),
            equivalentAssetNetPriceWithVAT: isPositive() |> V.when(showValuesInEuro && model.showAmountsWithVAT),
            additionalCosts: [isPositive() |> V.when(!model.showAmountsWithVAT)] |> V.stopOnFirstFailure,
            equivalentAdditionalCosts:
              [isPositive() |> V.when(showValuesInEuro && !model.showAmountsWithVAT)] |> V.stopOnFirstFailure,
            additionalCostsWithVAT: [isPositive() |> V.when(model.showAmountsWithVAT)] |> V.stopOnFirstFailure,
            equivalentAdditionalCostsWithVAT:
              [isPositive() |> V.when(showValuesInEuro && model.showAmountsWithVAT)] |> V.stopOnFirstFailure,
          })
        )
      ),
    });
  });
